import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Collapse, Modal, Button, Alert } from "react-bootstrap";

const BASE_URL = "https://admin.vascularirdoctors.com/backend";

const Doctors = () => {
  const [collapseBtn, setCollapseBtn] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  // ADD FORM
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    spec: "",
    designation: "",
    qualification: "",
    experience: "",
    patients: "",
    email: "",
    phone: "",
    location: "",
    about: "",
    expertise: "",
    services: "",
  });

  // ALERT
  const [alert, setAlert] = useState({
    show: false,
    type: "",
    text: "",
  });

  // DELETE
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // EDIT
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editImage, setEditImage] = useState(null);
  const [editPreview, setEditPreview] = useState("");

  const [editData, setEditData] = useState({
    name: "",
    slug: "",
    spec: "",
    designation: "",
    qualification: "",
    experience: "",
    patients: "",
    email: "",
    phone: "",
    location: "",
    about: "",
    expertise: "",
    services: "",
    image: "",
  });

  // ALERT FUNCTION
  const showAlert = (type, text) => {
    setAlert({ show: true, type, text });
    setTimeout(() => {
      setAlert({ show: false, type: "", text: "" });
    }, 3000);
  };

  // FETCH DOCTORS
  const fetchDoctors = async () => {
    try {
      const res = await fetch(`${BASE_URL}/doctors`);
      const data = await res.json();
      setDoctors(data);
    } catch (err) {
      console.log("FETCH DOCTORS ERROR:", err);
      showAlert("danger", "Unable to fetch doctors.");
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  // SLUG GENERATOR
  const generateSlug = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  // CHANGE FORM
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setFormData({
        ...formData,
        name: value,
        slug: generateSlug(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    setImage(file);

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // RESET ADD FORM
  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      spec: "",
      designation: "",
      qualification: "",
      experience: "",
      patients: "",
      email: "",
      phone: "",
      location: "",
      about: "",
      expertise: "",
      services: "",
    });
    setImage(null);
    setImagePreview("");
  };

  // ADD DOCTOR
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.slug) {
      showAlert("danger", "Doctor name and slug are required.");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });

      if (image) {
        data.append("image", image);
      }

      const res = await fetch(`${BASE_URL}/doctors`, {
        method: "POST",
        body: data,
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Doctor added successfully.");
        resetForm();
        fetchDoctors();
      } else {
        showAlert("danger", result.message || "Failed to add doctor.");
      }
    } catch (err) {
      console.log("ADD DOCTOR ERROR:", err);
      showAlert("danger", "Unable to connect to server.");
    }

    setLoading(false);
  };

  // DELETE
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const deleteDoctor = async () => {
    try {
      const res = await fetch(`${BASE_URL}/doctors/${deleteId}`, {
        method: "DELETE",
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Doctor deleted successfully.");
        fetchDoctors();
      } else {
        showAlert("danger", result.message || "Delete failed.");
      }
    } catch (err) {
      console.log("DELETE ERROR:", err);
      showAlert("danger", "Unable to delete doctor.");
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // OPEN EDIT
  const openEditModal = (doctor) => {
    setEditId(doctor.id);
    setEditData({
      name: doctor.name || "",
      slug: doctor.slug || "",
      spec: doctor.spec || "",
      designation: doctor.designation || "",
      qualification: doctor.qualification || "",
      experience: doctor.experience || "",
      patients: doctor.patients || "",
      email: doctor.email || "",
      phone: doctor.phone || "",
      location: doctor.location || "",
      about: doctor.about || "",
      expertise: doctor.expertise || "",
      services: doctor.services || "",
      image: doctor.image || "",
    });
    setEditImage(null);
    setEditPreview("");
    setShowModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setEditData({
        ...editData,
        name: value,
        slug: generateSlug(value),
      });
    } else {
      setEditData({
        ...editData,
        [name]: value,
      });
    }
  };

  const handleEditImage = (e) => {
    const file = e.target.files[0];
    setEditImage(file);

    if (file) {
      setEditPreview(URL.createObjectURL(file));
    }
  };

  // UPDATE
  const updateDoctor = async () => {
    if (!editData.name || !editData.slug) {
      showAlert("danger", "Doctor name and slug are required.");
      return;
    }

    try {
      const data = new FormData();

      Object.keys(editData).forEach((key) => {
        if (key !== "image") {
          data.append(key, editData[key]);
        }
      });

      if (editImage) {
        data.append("image", editImage);
      }

      const res = await fetch(`${BASE_URL}/doctors/${editId}`, {
        method: "PUT",
        body: data,
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Doctor updated successfully.");
        setShowModal(false);
        fetchDoctors();
      } else {
        showAlert("danger", result.message || "Update failed.");
      }
    } catch (err) {
      console.log("UPDATE ERROR:", err);
      showAlert("danger", "Unable to update doctor.");
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-xl-12">
          <div className="row page-titles align-items-center">
            <div className="col-sm-6">
              <ol className="breadcrumb mt-sm-0 mt-2">
                
                <li className="breadcrumb-item active">Doctors</li>
              </ol>
            </div>

            <div className="col-sm-6 text-sm-end mt-2 mt-sm-0">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => setCollapseBtn(!collapseBtn)}
              >
                <i className={`fas ${collapseBtn ? "fa-times" : "fa-plus"} me-1`}></i>
                {collapseBtn ? " Close" : " Open Form"}
              </button>
            </div>
          </div>
        </div>

        {/* ALERT */}
        {alert.show && (
          <div className="col-xl-12">
            <Alert variant={alert.type} className="shadow-sm rounded-3">
              {alert.text}
            </Alert>
          </div>
        )}

        {/* ADD FORM */}
        <div className="col-xl-12">
          <div className="filter cm-content-box box-primary">
            <div className="content-title d-flex justify-content-between align-items-center">
              <div className="cpa">
                <i className="fas fa-user-md me-1"></i>Add Doctor
              </div>
              <div className="tools">
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() => setCollapseBtn(!collapseBtn)}
                >
                  <i
                    className={`fas ${
                      collapseBtn ? "fa-angle-up" : "fa-angle-down"
                    }`}
                  ></i>
                </button>
              </div>
            </div>

            <Collapse in={collapseBtn}>
              <div className="cm-content-body form excerpt">
                <div className="card-body">
                  <form onSubmit={submitHandler}>
                    <div className="row">
                      {/* LEFT */}
                      <div className="col-xl-6">
                        <div className="mb-3">
                          <label className="form-label">Doctor Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter doctor name"
                            required
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Slug *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="slug"
                            value={formData.slug}
                            onChange={handleChange}
                            placeholder="doctor-slug"
                            required
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Specialization</label>
                          <input
                            type="text"
                            className="form-control"
                            name="spec"
                            value={formData.spec}
                            onChange={handleChange}
                            placeholder="Interventional Radiologist"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Designation</label>
                          <input
                            type="text"
                            className="form-control"
                            name="designation"
                            value={formData.designation}
                            onChange={handleChange}
                            placeholder="Senior Consultant"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Qualification</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            name="qualification"
                            value={formData.qualification}
                            onChange={handleChange}
                            placeholder="MBBS, MD..."
                          ></textarea>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Experience</label>
                          <input
                            type="text"
                            className="form-control"
                            name="experience"
                            value={formData.experience}
                            onChange={handleChange}
                            placeholder="10+ Years"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Patients Treated</label>
                          <input
                            type="text"
                            className="form-control"
                            name="patients"
                            value={formData.patients}
                            onChange={handleChange}
                            placeholder="5000+ Patients"
                          />
                        </div>
                      </div>

                      {/* RIGHT */}
                      <div className="col-xl-6">
                        <div className="mb-3">
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="doctor@example.com"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Phone</label>
                          <input
                            type="text"
                            className="form-control"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 9876543210"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Location</label>
                          <input
                            type="text"
                            className="form-control"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Hyderabad"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">About Doctor</label>
                          <textarea
                            className="form-control"
                            rows="5"
                            name="about"
                            value={formData.about}
                            onChange={handleChange}
                            placeholder="Write short biography..."
                          ></textarea>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">
                            Expertise (comma separated)
                          </label>
                          <textarea
                            className="form-control"
                            rows="3"
                            name="expertise"
                            value={formData.expertise}
                            onChange={handleChange}
                            placeholder="Interventional Radiology, Vascular Procedures, Tumor Ablation"
                          ></textarea>
                        </div>
                      </div>

                      {/* SERVICES */}
                      <div className="col-xl-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Services (Use this format)
                          </label>
                          <textarea
                            className="form-control"
                            rows="5"
                            name="services"
                            value={formData.services}
                            onChange={handleChange}
                            placeholder="Varicose Veins Treatment::Advanced minimally invasive procedure|Tumor Ablation::Precision targeted treatment"
                          ></textarea>
                          <small className="text-muted">
                            Use format: <b>Title::Description | Title::Description</b>
                          </small>
                        </div>
                      </div>

                      {/* IMAGE */}
                      <div className="col-xl-6">
                        <div className="mb-3">
                          <label className="form-label">Upload Doctor Image</label>
                          <input
                            type="file"
                            className="form-control"
                            onChange={handleImage}
                            accept="image/*"
                          />
                        </div>
                      </div>

                      <div className="col-xl-6">
                        {imagePreview && (
                          <div className="mb-3">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                width: "100%",
                                maxHeight: "220px",
                                objectFit: "cover",
                                borderRadius: "12px",
                                border: "1px solid #ddd",
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div className="col-xl-12 text-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Save Doctor"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </Collapse>
          </div>
        </div>

        {/* DOCTOR LIST */}
        <div className="col-xl-12 mt-4">
          <div className="filter cm-content-box box-primary">
            <div className="content-title">
              <div className="cpa">
                <i className="fas fa-list me-1"></i>Uploaded Doctors
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Slug</th>
                      <th>Specialization</th>
                      <th>Experience</th>
                      <th>Location</th>
                      <th style={{ width: "170px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.length > 0 ? (
                      doctors.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <img
                              src={
                                item.image
                                  ? `${BASE_URL}${item.image}`
                                  : "https://via.placeholder.com/90x60?text=No+Image"
                              }
                              alt={item.name}
                              style={{
                                width: "90px",
                                height: "60px",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                            />
                          </td>
                          <td>{item.name}</td>
                          <td>{item.slug}</td>
                          <td>{item.spec}</td>
                          <td>{item.experience}</td>
                          <td>{item.location}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-info me-2"
                              onClick={() => openEditModal(item)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => confirmDelete(item.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No doctors found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Doctor</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Doctor Name</label>
              <input
                className="form-control"
                name="name"
                value={editData.name}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Slug</label>
              <input
                className="form-control"
                name="slug"
                value={editData.slug}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Specialization</label>
              <input
                className="form-control"
                name="spec"
                value={editData.spec}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Designation</label>
              <input
                className="form-control"
                name="designation"
                value={editData.designation}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Qualification</label>
              <textarea
                className="form-control"
                rows="3"
                name="qualification"
                value={editData.qualification}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Experience</label>
              <input
                className="form-control"
                name="experience"
                value={editData.experience}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Patients Treated</label>
              <input
                className="form-control"
                name="patients"
                value={editData.patients}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Email</label>
              <input
                className="form-control"
                name="email"
                value={editData.email}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Phone</label>
              <input
                className="form-control"
                name="phone"
                value={editData.phone}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Location</label>
              <input
                className="form-control"
                name="location"
                value={editData.location}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>About Doctor</label>
              <textarea
                className="form-control"
                rows="4"
                name="about"
                value={editData.about}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Expertise</label>
              <textarea
                className="form-control"
                rows="3"
                name="expertise"
                value={editData.expertise}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Services</label>
              <textarea
                className="form-control"
                rows="4"
                name="services"
                value={editData.services}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Change Image</label>
              <input
                type="file"
                className="form-control"
                onChange={handleEditImage}
                accept="image/*"
              />
            </div>

            <div className="col-md-12">
              <img
                src={
                  editPreview
                    ? editPreview
                    : editData.image
                    ? `${BASE_URL}${editData.image}`
                    : "https://via.placeholder.com/600x250?text=No+Image"
                }
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: "250px",
                  objectFit: "cover",
                  borderRadius: "10px",
                }}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={updateDoctor}>
            Update Doctor
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Doctor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this doctor? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteDoctor}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Doctors;